import React, { PureComponent, Fragment } from 'react';
import { Card, CardBody, Col, Badge } from 'reactstrap';
import { connect } from 'react-redux';

import ExpiredListingsTableHead from './ExpiredListingsTableHead';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import { Tooltip } from '@material-ui/core';
import moment from "moment";

import LinearLoading from '../../../shared/components/LinearLoading';
import Button from '@material-ui/core/Button';
import LoadMoreIcon from 'mdi-react/RefreshIcon';
import LoadingIcon from 'mdi-react/DotsHorizontalIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import ArrowUpIcon from 'mdi-react/ChevronUpCircleOutlineIcon';
import ArrowDownIcon from 'mdi-react/ChevronDownCircleOutlineIcon';
import ArrowEqualIcon from 'mdi-react/MinusCircleOutlineIcon';

import SkipTraceUnifiedModal from '../../../shared/components/skiptrace/SkipTraceUnifiedModal';
import UnitIcons from '../../../shared/components/UnitIcons';

import {
  isMobile,
} from "react-device-detect";

import {
	_capitalizeText,
	_formatNumber,
	_formatPrice,
	_nameToUrl,
	_getBuildingURL,
	_replaceAbbreviations,
	_objectEqual,
	_formatUnitNumber,
	HouseCurrentStatus,
	_getFirstAndLastFromFullName,
	_extractIndividualNames,
	_isHouse,
	OwnershipTypeBadge,
} from "../../../shared/helpers/utils";

import { _axiosCall } from '../../../shared/helpers/apicalls';
const buildingUrl = process.env.API_ROOT + '/query/';

// Helper function to get broker/agent info from search-listings contacts_json
const getListingBrokerInfo = (property) => {
	try {
		if (!property._listingData || !property._listingData.contacts_json) return null;
		let contactsRaw = property._listingData.contacts_json;
		// Handle both string and already-parsed formats
		let contacts = typeof contactsRaw === 'string' ? JSON.parse(contactsRaw) : contactsRaw;
		// Normalize to array
		if (!Array.isArray(contacts)) contacts = [contacts];
		if (contacts.length === 0) return null;

		// Extract company/broker name from first contact
		let companyName = null;
		for (let i = 0; i < contacts.length && !companyName; i++) {
			const c = contacts[i];
			if (c.company) {
				if (Array.isArray(c.company) && c.company[0]) {
					companyName = typeof c.company[0] === 'string' ? c.company[0] : (c.company[0].name || null);
				} else if (typeof c.company === 'string') {
					companyName = c.company;
				} else if (c.company.name) {
					companyName = c.company.name;
				}
			} else if (c.office_name) {
				companyName = c.office_name;
			} else if (c.broker) {
				companyName = typeof c.broker === 'string' ? c.broker : (c.broker.name || null);
			}
		}
		if (companyName) companyName = _capitalizeText(companyName);

		const agentList = contacts
			.map(c => ({
				name: c.name ? _capitalizeText(c.name) : (c.full_name ? _capitalizeText(c.full_name) : null),
				email: c.email || null
			}))
			.filter(a => a.name || a.email);

		return { brokerName: companyName, agentList };
	} catch (e) {
		console.log("getListingBrokerInfo error:", e);
	}
	return null;
};

// Helper function to get broker/agent info from off-market data JSON
const getOffMarketBrokerInfo = (property) => {
	try {
		if (property.last_listed_sale_off_market_data) {
			const data = JSON.parse(property.last_listed_sale_off_market_data);
			const brokerName = data.broker_display_name ? _capitalizeText(data.broker_display_name) : null;
			const agents = Array.isArray(data.agent) ? data.agent : data.agent ? [data.agent] : [];
			const agentList = agents
				.map(a => ({ name: a.name || null, email: a.email || null }))
				.filter(a => a.name || a.email);
			return { brokerName, agentList };
		}
	} catch (e) {
		// invalid JSON, ignore
	}
	return null;
};

// Helper function to get off-market info
const getOffMarketInfo = (property) => {
	const dates = [
		{ type: 'TOM', date: property.last_listed_sale_tom_date_formatted, label: 'Temporarily Off Market', color: '#FFA500', bgColor: '#FFF3E0' },
		{ type: 'POM', date: property.last_listed_sale_pom_date_formatted, label: 'Permanently Off Market', color: '#DC3545', bgColor: '#FFEBEE' },
		{ type: 'Expired', date: property.last_listed_sale_expired_date_formatted, label: 'Listing Expired', color: '#6C757D', bgColor: '#F5F5F5' }
	].filter(d => d.date && moment(d.date).isValid());

	if (!dates.length) return null;
	return dates.sort((a, b) => moment(b.date) - moment(a.date))[0];
};

// Helper function to calculate days on market
const formatDaysOnMarket = (days) => {
	if (days === null || days === undefined) return '--';
	if (days >= 365) {
		const years = Math.floor(days / 365);
		const remainingDays = days % 365;
		return remainingDays > 0 ? `${years} yr, ${remainingDays} days` : `${years} yr`;
	}
	return `${days} days`;
};

const calculateDaysOnMarket = (property) => {
	const listDate = property.last_listed_sale_date;
	if (!listDate) return null;

	const offMarketInfo = getOffMarketInfo(property);
	if (!offMarketInfo) return null;

	const typeToTimestampField = {
		'TOM': 'last_listed_sale_tom_date',
		'POM': 'last_listed_sale_pom_date',
		'Expired': 'last_listed_sale_expired_date'
	};

	const offMarketTimestamp = property[typeToTimestampField[offMarketInfo.type]];
	if (!offMarketTimestamp) return null;

	const diffSeconds = offMarketTimestamp - listDate;
	if (diffSeconds < 0) return null;

	return Math.round(diffSeconds / 86400);
};

// Helper function to calculate price change
const calculatePriceChange = (property) => {
	const originalPrice = property.first_listed_price || property.offering_price_initial;
	const lastPrice = property.last_listed_sale_price_all;

	if (!originalPrice || !lastPrice || originalPrice <= 0) return null;

	return ((lastPrice - originalPrice) / originalPrice * 100);
};

// Badge component for off-market type
const OffMarketTypeBadge = ({ info }) => {
	if (!info) return <span className="light-text small-text">--</span>;

	return (
		<Tooltip title={info.label} placement="top">
			<Badge
				style={{
					backgroundColor: info.bgColor,
					color: info.color,
					padding: '4px 8px',
					fontSize: '11px',
					fontWeight: 500
				}}
			>
				{info.type}
			</Badge>
		</Tooltip>
	);
};

// Price change indicator component
const PriceChangeIndicator = ({ change }) => {
	if (change === null || change === undefined) return <span className="light-text small-text">--</span>;

	const isPositive = change > 0;
	const isNegative = change < 0;
	const formattedChange = Math.abs(change).toFixed(1) + '%';

	if (isPositive) {
		return (
			<span style={{ color: '#28a745', display: 'flex', alignItems: 'center' }}>
				<ArrowUpIcon size={16} /> +{formattedChange}
			</span>
		);
	} else if (isNegative) {
		return (
			<span style={{ color: '#dc3545', display: 'flex', alignItems: 'center' }}>
				<ArrowDownIcon size={16} /> -{formattedChange}
			</span>
		);
	} else {
		return (
			<span style={{ color: '#6c757d', display: 'flex', alignItems: 'center' }}>
				<ArrowEqualIcon size={16} /> 0%
			</span>
		);
	}
};

class ExpiredListingsListTable extends PureComponent {
	state = {
		order: "",
		orderBy: "",
		selected: new Map([]),
		page: 0,
		rowsPerPage: 20,
		fetchInProgress: false,
		buildingKey: false,
		unitListData: [],
		start: 0,
		noMoreResults: false,
		selectedUnits: {},
		openModalKey: null
	};

	componentDidMount() {
		if (this.props.buildingKey) {
			this.queryUnits();
		}
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (this.props.buildingKey) {
			if (!_objectEqual(this.props.q, prevProps.q) ||
				(this.props.buildingKey !== prevProps.buildingKey ||
					this.state.orderBy !== prevState.orderBy ||
					this.state.order !== prevState.order ||
					this.state.start !== prevState.start)) {

				const reset = this.state.orderBy !== prevState.orderBy || this.state.order !== prevState.order;
				this.queryUnits(reset);
			}
		}
	}

	_loadMore = () => {
		if (this.state.fetchInProgress === true) {
			return false;
		}
		this.setState({ start: (this.state.start + this.state.rowsPerPage) });
	}

	_handleRequestUnitsSort = (event, property) => {
		const orderBy = property;
		let order = 'desc';

		const { orderBy: stateOrderBy, order: stateOrder } = this.state;

		if (stateOrderBy === property && stateOrder === 'desc') { order = 'asc'; }

		this.setState({ order, orderBy, start: 0 });
	};

	queryUnits = async (reset) => {
		if (!this.state.fetchInProgress) {
			this.setState(prevState => ({ fetchInProgress: true, }));
		}

		const { buildingKey, q, fq, fl } = this.props;
		const { orderBy, order } = this.state;

		let sort = "last_listed_sale_expired_date_formatted desc, last_listed_sale_pom_date_formatted desc, last_listed_sale_tom_date_formatted desc";
		if (this.state.orderBy && this.state.orderBy.length > 0) {
			if (orderBy === 'address_2_normalized') {
				sort = `floor ${order}, ${orderBy} ${order}`;
			} else if (orderBy === 'off_market_date') {
				sort = `last_listed_sale_expired_date_formatted ${order}, last_listed_sale_pom_date_formatted ${order}, last_listed_sale_tom_date_formatted ${order}`;
			} else {
				sort = `${orderBy} ${order}`;
			}
		}

		let unitsFQ = [...fq, `building:${buildingKey}`];

		let axiosData = {
			url: buildingUrl + "we3-properties",
			method: "post",
			query: {
				q: q,
				fq: unitsFQ,
				fl: fl,
				sort: sort,
				start: reset ? 0 : this.state.start,
				rows: this.state.rowsPerPage,
				wt: "json"
			}
		};

		let response = await _axiosCall(axiosData)
			.then(res => {
				return res;
			})
			.catch(error => {
				console.log("error: " + error);
			});

		let formattedData = response && response.docs ? response.docs : [];

		if (reset) {
			this.setState({
				unitListData: formattedData,
				fetchInProgress: false,
				noMoreResults: formattedData.length < this.state.rowsPerPage
			});
		} else {
			this.setState({
				unitListData: [...this.state.unitListData, ...formattedData],
				fetchInProgress: false,
				noMoreResults: formattedData.length < this.state.rowsPerPage
			});
		}
	}

	_handleSelectAllClick = () => {
		const { handleSelectParentAll, selectedUnitsAll } = this.props;
		const { unitListData } = this.state;
		const data = this.props.isGrouped ? unitListData : this.props.tableData;
		if(!data || !handleSelectParentAll) return;
		const currentPageSelectedCount = data.filter(item => selectedUnitsAll[item.key] !== undefined).length;
		if(currentPageSelectedCount < data.length){
			handleSelectParentAll(data, "add");
		}else{
			handleSelectParentAll(data, "remove");
		}
	}

	_selectUnit = (d) => {
		const { handleSelectParent } = this.props;
		if(handleSelectParent) handleSelectParent(d);
	}

	handleOpenModal = (key) => {
		this.setState({ openModalKey: key });
	};

	handleCloseModal = () => {
		this.setState({ openModalKey: null });
	};

	render() {
		const {
			order, orderBy, selected, rowsPerPage, page, tableData, reactTableFiltered,
			handleRequestSort, handleSelectAllClick, handleClick, isSelected, handleBuildingClick,
			building, isGrouped, selectedUnitsAll, user, subFilters
		} = this.props;

		const { unitListData, fetchInProgress, noMoreResults } = this.state;

		let data = isGrouped ? unitListData : tableData;
		const currentPageSelectedCount = data ? data.filter(d => selectedUnitsAll[d.key] !== undefined).length : 0;

		if (!data) {
			return (
				<Col md={12} lg={12}>
					<Card>
						<CardBody className={'progress__card'}>
							<LinearLoading />
						</CardBody>
					</Card>
				</Col>
			);
		}

		if (data.length <= 0) {
			return (
				<Col md={12} lg={12}>
					<Card>
						<CardBody>
							<div className="email-confirmation">
								<div className="email-confirmation__icon">
									<i className="lnr lnr-cross-circle modal__close-icon" style={{ fontSize: "48px", opacity: ".5" }}></i>
								</div>
								<h3 className="email-confirmation__title">No Properties Found</h3>
								<p className="email-confirmation__sub">Try adjusting your search criteria.</p>
							</div>
						</CardBody>
					</Card>
				</Col>
			);
		}

		return (
			<Fragment>
				<div>
					<Table className="material-table">
						<ExpiredListingsTableHead
							numSelected={currentPageSelectedCount}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={this._handleSelectAllClick.bind(this)}
							onRequestSort={isGrouped ? this._handleRequestUnitsSort : handleRequestSort}
							rowCount={data.length}
							searchMode={this.props.searchMode}
						/>
						<TableBody>
							{data.map((d) => {
								const itemSelected = isSelected(d.id);
								const buildingData = d;
								var owners = _extractIndividualNames(d.owner);
								var self = this;
								var isHouse = _isHouse(d);

								if (owners.length === 0 && d && d.owner) {
									owners = [String(d.owner)];
								}

								if (d.baths % 1 == 0 && d.half_baths > 0) {
									d.baths = d.baths + .5;
								}

								const isOnMarket = this.props.searchMode === "on_market";
								const offMarketInfo = isOnMarket ? null : getOffMarketInfo(d);
								const daysOnMarket = isOnMarket
									? (d.last_listed_sale_date_all ? Math.round((Date.now() / 1000 - d.last_listed_sale_date_all) / 86400) : null)
									: calculateDaysOnMarket(d);
								const priceChange = calculatePriceChange(d);

								return (
									<TableRow
										className="material-table__row"
										role="checkbox"
										onClick={event => handleBuildingClick(event, d)}
										aria-checked={itemSelected}
										tabIndex={-1}
										key={d.id}
										selected={selectedUnitsAll[d.key] !== undefined}
									>
										<TableCell className="material-table__cell" padding="checkbox">
									<Checkbox checked={selectedUnitsAll[d.key] !== undefined} onClick={(e) => { e.preventDefault(); e.stopPropagation(); this._selectUnit(d); }} className={`material-table__checkbox ${selectedUnitsAll[d.key] !== undefined && 'material-table__checkbox--checked'}`} />
								</TableCell>
								{!isMobile && <TableCell
											className="material-table__cell material-table__cell-right"
											size="small"
										>
											<HouseCurrentStatus status={d.current_status} unit={d} />
										</TableCell>}

										<TableCell
											className="material-table__cell material-table__cell-right nowrap"
											component="td"
											scope="row"
											padding=""
										>
											{isGrouped || this.props.buildingView ?
												<div className="icon__floorplan">
													<span>{_formatUnitNumber(d.address_2_normalized, building).replace("#", "Apt")}</span>
													<UnitIcons building={building} unit={d} />
													<Fragment>
														<OpenInNewIcon size={14} style={{ marginTop: "-3px", marginLeft: "5px" }} color={'#BFC4D1'} onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`${_getBuildingURL(buildingData, 'building_slugs')}/${_nameToUrl(d.address_2_normalized)}`); }} />
													</Fragment>
												</div>
												:
												<div className="icon__floorplan">
													<span>{d.name ? _capitalizeText(d.name) : _capitalizeText(_replaceAbbreviations(d.display_full_street_address))}{isHouse ? "" : ", " + d.address_2_normalized.toUpperCase().replace("APT", "Apt")}</span>
													<OwnershipTypeBadge ownership_type={d.ownership_type} />
													{!isMobile && <Fragment>
														<OpenInNewIcon size={14} style={{ marginTop: "-3px", marginLeft: "5px" }} color={'#BFC4D1'} onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`${isHouse ? _getBuildingURL(buildingData, 'building_slugs') : _getBuildingURL(buildingData, 'building_slugs')}/${_nameToUrl(d.address_2_normalized)}`); }} />
													</Fragment>}
													<br /><span className="light-text small-text" style={{ color: "#858AA1" }}>{d.neighborhood && d.neighborhood[0] ? `${_capitalizeText(d.neighborhood[0])}, ` : ''} {_capitalizeText(d.borough)}</span>
												</div>
											}
										</TableCell>

										<TableCell
											className="material-table__cell material-table__cell-right"
											size="small"
											style={{ minWidth: 250, maxWidth: 400, whiteSpace: "normal" }}
										>
											<div style={{display:'inline-flex',alignItems:'flex-start',gap:6}}>
												{owners.map((name, idx) => {
													var person = _getFirstAndLastFromFullName(name);
													var displayName = person.display ? person.display : [person.first_name, person.middle_name, person.last_name]
														.filter(Boolean)
														.join(' ');
													var modalKey = d.id + '-' + idx;
													return (
														<SkipTraceUnifiedModal
															user={user}
															key={'stm-' + idx}
															first_name={'' + person.first_name}
															last_name={'' + person.last_name}
															address_1={'' + d.display_full_street_address}
															address_2={'' + d.address_2_normalized}
															city={'' + d.city}
															county={'' + d.county}
															state={'' + d.state}
															zip={'' + d.zip}
															triggerDisplay={_capitalizeText(displayName)}
															borough_code={d.borough_code}
															block={d.block}
															lot={d.lot}
															property={d}
															isOpen={this.state.openModalKey === modalKey}
															onOpen={() => this.handleOpenModal(modalKey)}
															onClose={this.handleCloseModal}
														/>
													);
												})}
												{d.absentee_owner && (
													<span style={{padding:'1px 8px',borderRadius:15,fontSize:11,background:'rgba(255,200,0,0.18)',color:'#7a5c00',border:'1px solid rgba(255,200,0,0.45)',fontWeight:500,whiteSpace:'nowrap'}}>Absentee</span>
												)}
												{/* Unmasked pill removed — SkipTraceUnifiedModal trigger now shows the signatory name with the LLC in parens */}
												{/* {d.llc_unmasked && (
													<span title={d.llc_signatory_name} style={{padding:'1px 8px',borderRadius:15,fontSize:11,background:'rgba(0,170,255,0.13)',color:'#0059a3',border:'1px solid rgba(0,170,255,0.35)',fontWeight:500,whiteSpace:'nowrap'}}>Unmasked</span>
												)} */}
											</div>
										</TableCell>

										{isOnMarket ? (
										<TableCell
											className="material-table__cell material-table__cell-right"
											size="small"
										>
											{d.last_listed_sale_date_all ?
												<Fragment>
													<span className="light-text small-text">{moment.unix(d.last_listed_sale_date_all).format('MM/DD/YYYY')}</span>
													<br /><span className="light-text xsmall-text" style={{ color: "#858AA1" }}>{moment.unix(d.last_listed_sale_date_all).fromNow()}</span>
												</Fragment>
												:
												<span className="light-text small-text">--</span>
											}
										</TableCell>
									) : (
										<Fragment>
											<TableCell
												className="material-table__cell material-table__cell-right"
												size="small"
											>
												{offMarketInfo ?
													<Fragment>
														<span className="light-text small-text">{moment(offMarketInfo.date).format('MM/DD/YYYY')}</span>
														<br /><span className="light-text xsmall-text" style={{ color: "#858AA1" }}>{moment(offMarketInfo.date).fromNow()}</span>
													</Fragment>
													:
													<span className="light-text small-text">--</span>
												}
											</TableCell>

											<TableCell
												className="material-table__cell material-table__cell-right"
												size="small"
											>
												<OffMarketTypeBadge info={offMarketInfo} />
											</TableCell>
										</Fragment>
									)}

										<TableCell
											className="material-table__cell material-table__cell-right"
											size="small"
										>
											{daysOnMarket !== null ?
												<Tooltip title={`${daysOnMarket} days on market`} placement="top">
													<span className={`light-text small-text ${daysOnMarket > 180 ? 'text-danger' : ''}`}>
														{formatDaysOnMarket(daysOnMarket)}
													</span>
												</Tooltip>
												:
												<span className="light-text small-text">--</span>
											}
										</TableCell>

										<TableCell
											className="material-table__cell material-table__cell-right"
											size="small"
										>
											<span className="light-text small-text">
												{d.last_listed_sale_price_all > 0 ? _formatPrice(d.last_listed_sale_price_all) : '--'}
											</span>
										</TableCell>

										<TableCell
											className="material-table__cell material-table__cell-right"
											size="small"
										>
											{(() => {
												const info = isOnMarket ? getListingBrokerInfo(d) : getOffMarketBrokerInfo(d);
												if (!info || (!info.brokerName && info.agentList.length === 0)) return <span className="light-text small-text">--</span>;
												return (
													<Fragment>
														{info.brokerName && <span className="light-text small-text">{info.brokerName}</span>}
														{info.agentList.length > 0 && <Fragment>
															{info.brokerName && <br />}
															{info.agentList.map((agent, i) => (
																<span key={i} className="light-text small-text" style={{ color: "#858AA1" }}>
																	{i > 0 && ', '}
																	{agent.name}
																	{agent.name && agent.email && ' - '}
																	{agent.email && <a href={`mailto:${agent.email}`} className="link-text" onClick={(e) => e.stopPropagation()}>{agent.email}</a>}
																</span>
															))}
														</Fragment>}
													</Fragment>
												);
											})()}
										</TableCell>

										<TableCell
											className="material-table__cell material-table__cell-right"
											size="small"
										>
											<span className="light-text small-text">{d.bedrooms > -1 ? d.bedrooms === 0 ? 'Studio' : d.bedrooms + ' Bed' : '--'}</span>
										</TableCell>

										<TableCell
											className="material-table__cell material-table__cell-right"
											size="small"
										>
											<span className="light-text small-text">{d.baths > -1 ? d.baths + ' Bath' : '--'}</span>
										</TableCell>

										<TableCell
											className="material-table__cell material-table__cell-right"
											size="small"
										>
											<span className="light-text small-text">{d.sq_ft > 0 ? _formatNumber(d.sq_ft) + ' SF' : '--'}</span>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
					{isGrouped && !noMoreResults &&
						<div className="unit__load">
							<Tooltip title="Load more units">
								<Button onClick={this._loadMore.bind(this)} variant="contained" color="news" className={`timeline__load-more`} startIcon={fetchInProgress ? <LoadingIcon className={'refresh-spin'} /> : <LoadMoreIcon />}>
									{fetchInProgress ? 'Loading...' : 'Load More'}
								</Button>
							</Tooltip>
						</div>
					}
				</div>
			</Fragment>
		);
	}
}

export default connect(state => ({
	user: state.user,
}))(ExpiredListingsListTable);
